import React from "react";
import { ExternalLink, TrendingUp, TrendingDown, AlertCircle } from "lucide-react";

// Source: WHO "The top 10 causes of death" fact sheet (7 August 2024).
// Data year: 2021. URL: https://www.who.int/news-room/fact-sheets/detail/the-top-10-causes-of-death
const CAUSES = [
    {
        rank: 1,
        name: "Ischaemic heart disease",
        stat: "9.1M deaths · 13% of global total",
        trend: "up",
        trendLabel: "+2.7M since 2000",
        category: "Antihypertensives, Antidiabetics, Analgesics",
    },
    {
        rank: 2,
        name: "COVID-19",
        stat: "8.8M deaths",
        trend: "new",
        trendLabel: "Newly emerged",
        category: "Vaccines, Respiratory care, Hospital ICU",
    },
    {
        rank: 3,
        name: "Stroke",
        stat: "~10% of global deaths",
        trend: "flat",
        trendLabel: "Top-3 persistent",
        category: "Antihypertensives, Neuro CNS",
    },
    {
        rank: 4,
        name: "Chronic obstructive pulmonary disease",
        stat: "~5% of global deaths",
        trend: "flat",
        trendLabel: "Top-4 persistent",
        category: "Respiratory formulations, Hospital gas plants",
    },
    {
        rank: 5,
        name: "Lower respiratory infections",
        stat: "2.5M deaths",
        trend: "down",
        trendLabel: "−370k since 2000",
        category: "Antibacterials, Antifungals",
    },
    {
        rank: 6,
        name: "Trachea, bronchus & lung cancers",
        stat: "1.9M deaths",
        trend: "up",
        trendLabel: "+0.7M since 2000",
        category: "Nutraceuticals, Immunity, Hospital imaging",
    },
    {
        rank: 7,
        name: "Alzheimer's & other dementias",
        stat: "1.8M deaths · 68% women",
        trend: "up",
        trendLabel: "Rising sharply in HIC",
        category: "Neuro CNS (e.g. carbamazepine), Geriatric care",
    },
    {
        rank: 8,
        name: "Diabetes mellitus",
        stat: "+95% deaths since 2000",
        trend: "up",
        trendLabel: "Rising across income groups",
        category: "Antidiabetics — Metformin, Acarbose, Glimepiride",
    },
    {
        rank: 9,
        name: "Kidney diseases",
        stat: "+95% deaths since 2000",
        trend: "up",
        trendLabel: "Up from #19 to #9",
        category: "Antihypertensives, Antidiabetics, Dialysis consumables",
    },
    {
        rank: 10,
        name: "Tuberculosis",
        stat: "Top-10 in LIC & LMIC",
        trend: "down",
        trendLabel: "Declining globally",
        category: "Antibacterials, Vaccines (BCG), Diagnostics",
    },
];

const HIGHLIGHTS = [
    {
        figure: "39M",
        label: "Deaths from top 10 causes (2021)",
    },
    {
        figure: "57%",
        label: "Share of the world's 68M total deaths",
    },
    {
        figure: "7 / 10",
        label: "Top causes are noncommunicable diseases",
    },
    {
        figure: "68%",
        label: "Of the top 10 deaths fall in NCDs only",
    },
];

function TrendIcon({ t }) {
    const common = { size: 14, strokeWidth: 1.8 };
    if (t === "up")
        return (
            <TrendingUp
                {...common}
                className="text-red-600"
                aria-label="Rising"
            />
        );
    if (t === "down")
        return (
            <TrendingDown
                {...common}
                className="text-[#4D7C0F]"
                aria-label="Falling"
            />
        );
    if (t === "new")
        return (
            <AlertCircle
                {...common}
                className="text-amber-600"
                aria-label="New"
            />
        );
    return <span className="w-3 h-px bg-stone-400 inline-block align-middle" />;
}

export default function HealthFacts() {
    return (
        <section
            id="facts"
            className="py-24 md:py-32 border-b border-stone-200"
            data-testid="health-facts-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
                    <div className="md:col-span-7">
                        <div className="overline mb-4">
                            <span className="inline-block w-8 h-px bg-stone-400 align-middle mr-3" />
                            Facts Sheet · WHO 2024
                        </div>
                        <h2 className="font-cabinet font-bold text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
                            The world's top 10 causes of death —
                            <br />
                            <span className="italic font-normal text-[#0C4A6E]">
                                and what we export against them.
                            </span>
                        </h2>
                    </div>
                    <div className="md:col-span-5">
                        <p className="text-base md:text-lg text-stone-700 leading-relaxed">
                            Data from the WHO's <em>Top 10 causes of death</em>{" "}
                            fact sheet (August 2024, 2021 data). Momia's product
                            portfolio is engineered to answer these global
                            health priorities — from cardiovascular molecules
                            and antidiabetics to respiratory care, vaccines and
                            hospital infrastructure.
                        </p>
                        <a
                            href="https://www.who.int/news-room/fact-sheets/detail/the-top-10-causes-of-death"
                            target="_blank"
                            rel="noreferrer"
                            className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-[#0C4A6E] hover:text-[#072F49] border-b border-[#0C4A6E]/30 hover:border-[#0C4A6E] pb-1 transition-colors"
                            data-testid="who-source-link"
                        >
                            Read the WHO source
                            <ExternalLink size={14} strokeWidth={1.5} />
                        </a>
                    </div>
                </div>

                {/* Highlight strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-y border-stone-200 bg-[#F5F5F4] mb-12">
                    {HIGHLIGHTS.map((h, i) => (
                        <div
                            key={h.label}
                            className={`p-6 md:p-8 ${i < 3 ? "border-r border-stone-200" : ""} ${i === 1 || i === 3 ? "md:border-r-0" : ""}`}
                            data-testid={`highlight-${i}`}
                        >
                            <div className="font-cabinet font-black text-4xl md:text-5xl tracking-tight text-ink">
                                {h.figure}
                            </div>
                            <div className="mt-2 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-stone-600">
                                {h.label}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Causes table */}
                <div
                    className="bg-white border border-stone-200 overflow-x-auto"
                    data-testid="causes-table"
                >
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-[#0C4A6E] text-white text-left font-mono text-[10px] uppercase tracking-[0.18em]">
                                <th className="p-4 w-16">#</th>
                                <th className="p-4">Cause of death</th>
                                <th className="p-4">Mortality (2021)</th>
                                <th className="p-4">Trend</th>
                                <th className="p-4">Momia exports that address it</th>
                            </tr>
                        </thead>
                        <tbody>
                            {CAUSES.map((c) => (
                                <tr
                                    key={c.rank}
                                    className="border-t border-stone-200 hover:bg-stone-50 transition-colors"
                                    data-testid={`cause-${c.rank}`}
                                >
                                    <td className="p-4 align-top">
                                        <span className="font-mono text-[11px] text-stone-500">
                                            {String(c.rank).padStart(2, "0")}
                                        </span>
                                    </td>
                                    <td className="p-4 align-top">
                                        <div className="font-cabinet font-bold text-ink text-base leading-tight">
                                            {c.name}
                                        </div>
                                    </td>
                                    <td className="p-4 align-top text-stone-700 font-mono text-xs">
                                        {c.stat}
                                    </td>
                                    <td className="p-4 align-top">
                                        <div className="inline-flex items-center gap-2 text-xs text-stone-600">
                                            <TrendIcon t={c.trend} />
                                            {c.trendLabel}
                                        </div>
                                    </td>
                                    <td className="p-4 align-top text-stone-700 text-sm">
                                        {c.category}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-stone-500">
                    <div className="font-mono uppercase tracking-[0.18em]">
                        Source: WHO Global Health Estimates · Fact sheet dated 7 August 2024
                    </div>
                    <a
                        href="#inquiry"
                        className="btn-ghost text-xs"
                        data-testid="facts-cta-inquiry"
                    >
                        Request a matching quotation →
                    </a>
                </div>
            </div>
        </section>
    );
}
